import { differenceInCalendarDays } from 'date-fns';

import { parseDate } from 'lib/date';
import { type ProgressUpdateOptions } from 'models/activities/ProgressUpdate';
import AttrValue, { AttrValueOptions } from 'models/AttrValue';
import { AssignedUserRole, LocationEpisodeUser } from 'models/LocationEpisodeUser';
import { AdmissionOptions } from 'models/reviews/Admission';
import AltcsApplication, { AltcsApplicationOptions } from 'models/reviews/AltcsApplication';
import Authorization, { AuthorizationOptions } from 'models/reviews/Authorization';
import ProjectedDischarge, { ProjectedDischargeOptions } from 'models/reviews/ProjectedDischarge';
import Referral, { ReferralOptions } from 'models/reviews/Referral';
import ReviewModelFactory from 'models/reviews/ReviewModelFactory';
import SafeReturn, { SafeReturnOptions } from 'models/reviews/SafeReturn';
import { ServiceRefusalOptions } from 'models/reviews/ServiceRefusal';

import Classification, { ClassificationOptions } from './Classification';
import Group, { GroupOptions } from './Group';
import Patient, { PatientOptions } from './Patient';
import QuestionTemplate, { QuestionTemplateOptions, QuestionTemplateType } from './QuestionTemplate';
import RehabState, { RehabStateOptions } from './RehabState';
import Review from './Review';
export interface LocationEpisodeOptions {
  id: string;
  active?: boolean;
  archived: boolean;
  currentRehabState: Partial<RehabStateOptions>;
  admittedOn: string | Date;
  admittedAt: string | Date | null;
  endDate: string | Date;
  episodeId: string;
  episodeClassifications: Partial<ClassificationOptions>[];
  hasNewChanges: boolean | null;
  hospital: Partial<GroupOptions>;
  lastValidPredecessor: Partial<LocationEpisodeOptions> | null;
  latest: boolean;
  lengthOfStay: number;
  groupId: string;
  group: Partial<GroupOptions>;
  owner: Partial<GroupOptions>;
  patient: Partial<PatientOptions>;
  planTypeClassification: Partial<ClassificationOptions> | null;
  physicianTeam: Partial<AttrValueOptions>;
  questionTemplates: Partial<QuestionTemplateOptions>[];
  eligibleForProgressUpdate?: boolean;
  rehabStates: Partial<RehabStateOptions>[];
  reviews: (
    | Partial<AdmissionOptions>
    | Partial<AltcsApplicationOptions>
    | Partial<AuthorizationOptions>
    | Partial<ProjectedDischargeOptions>
    | Partial<ServiceRefusalOptions>
    | Partial<SafeReturnOptions>
    | Partial<ReferralOptions>
  )[];
  startDate: string;
  onTrack: string | null;
  onTrackUpdatedAt: string | null;
  statusOutOfDate: boolean;
  assignedUsers: Partial<LocationEpisodeUser>[] | null;
  hasActiveServiceRefusals: boolean;
  latestProgressUpdate: Partial<ProgressUpdateOptions> | null;
  createdAt?: string;
  sharedRecords?: Partial<LocationEpisodeOptions>[];
  trackLos?: boolean;
  umAISummaryEnabled?: boolean;
  unacknowledgedEscalations?: number;
  unacknowledgedPriorityNotes?: number;
  hasActiveSafeReturn?: boolean;
  removedFromCensus?: boolean;
  keyDetails?: string | null;
}

function getDefaults(): LocationEpisodeOptions {
  return {
    id: '',
    archived: false,
    currentRehabState: new RehabState(),
    admittedOn: '',
    admittedAt: null,
    endDate: '',
    episodeId: '',
    hasNewChanges: false,
    hospital: {
      name: '',
    },
    lastValidPredecessor: {},
    latest: true,
    lengthOfStay: 0,
    groupId: '',
    group: {},
    owner: {},
    patient: {},
    episodeClassifications: [],
    planTypeClassification: null,
    physicianTeam: {
      name: '',
    },
    questionTemplates: [],
    eligibleForProgressUpdate: false,
    rehabStates: [new RehabState()],
    reviews: [],
    startDate: '',
    onTrack: null,
    onTrackUpdatedAt: null,
    statusOutOfDate: false,
    assignedUsers: null,
    hasActiveServiceRefusals: false,
    latestProgressUpdate: null,
    sharedRecords: [],
    umAISummaryEnabled: false,
    keyDetails: null,
  };
}
/**
 * LocationEpisode
 * @class LocationEpisode
 * @param {Partial<LocationEpisodeOptions>} [options={}]
 * @property {string} id - The id of the location episode
 * @property {boolean} active - Whether the location episode is active
 * @property {boolean} archived - Whether the location episode is archived
 * @property {RehabState} currentRehabState - The current rehab state of the location episode
 * @property {Date} admittedOn - The date the patient was admitted
 * @property {Date} endDate - The date the location episode ended
 * @property {string} episodeId - The id of the episode
 * @property {Classification[]} episodeClassifications - The classifications of the episode
 * @property {boolean} hasNewChanges - Whether the location episode has new changes
 * @property {Group | null} hospital - The hospital of the location episode
 * @property {LocationEpisode | null} lastValidPredecessor - The last valid predecessor of the location episode
 * @property {boolean} latest - Whether the location episode is the latest
 * @property {number} lengthOfStay - The length of stay of the location episode
 * @property {string} groupId - The id of the group
 * @property {Group} owner - The owner of the location episode
 * @property {Patient} patient - The patient of the location episode
 * @property {Classification | undefined} planTypeClassification - The plan type classification of the location episode
 * @property {AttrValue} physicianTeam - The physician team of the location episode
 * @property {QuestionTemplate[]} questionTemplates - The question templates of the location episode
 * @property {RehabState[]} rehabStates - The rehab states of the location episode
 * @property {Review[]} reviews - The reviews of the location episode
 * @property {string} startDate - The start date of the location episode
 * @property {string | null} onTrack - The on track of the location episode
 * @property {string | null} onTrackUpdatedAt - The on track updated at of the location episode
 * @property {boolean} statusOutOfDate - Whether the status of the location episode is out of date
 * @property {User | null} caseManager - The case manager of the location episode
 * @property {boolean} hasActiveServiceRefusals - Whether the location episode has active service refusals
 * @property trackLos boolean
 * @property umAISummaryEnabled boolean
 * @method stayDescriptor - The stay descriptor of the location episode
 * @method activeAuthorizationReview - The active authorization review of the location episode
 * @method projectedDischargeReview - The projected discharge review of the location episode
 * @method discharged - The discharged of the location episode
 * @method dischargedBeforeProjectedDate - The discharged before projected date of the location episode
 * @method dischargeOverdue - The discharge overdue of the location episode
 * @method progressTemplate - The progress template of the location episode
 * @method dischargeTemplate - The discharge template of the location episode
 * @method inTreatment - The in treatment of the location episode
 * @example const locationEpisode = new LocationEpisode({ id: '38ce0cea-f787-11ea-8e07-07fe8aa91684' });
 * @returns {LocationEpisode}
 */
export default class LocationEpisode {
  id: string;
  active?: boolean;
  archived: boolean;
  currentRehabState: RehabState;
  admittedOn: Date;
  admittedAt: Date | null;
  endDate: Date;
  episodeId: string;
  episodeClassifications: Classification[];
  hasNewChanges: boolean | null;
  hospital: Group;
  lastValidPredecessor: Partial<LocationEpisodeOptions> | null;
  latest: boolean;
  lengthOfStay: number;
  groupId: string;
  group: Group;
  owner: Group;
  patient: Patient;
  planTypeClassification: Classification | null;
  physicianTeam: AttrValue;
  questionTemplates: QuestionTemplate[];
  eligibleForProgressUpdate?: boolean;
  rehabStates: RehabState[];
  reviews: Review[];
  startDate: string;
  onTrack: string | null;
  onTrackUpdatedAt: string | null;
  statusOutOfDate: boolean;
  assignedUsers: LocationEpisodeUser[] | null;
  hasActiveServiceRefusals: boolean;
  createdAt: string;
  sharedRecords: LocationEpisode[];
  latestProgressUpdate: Partial<ProgressUpdateOptions> | null;
  trackLos?: boolean;
  umAISummaryEnabled?: boolean;
  unacknowledgedEscalations: number;
  unacknowledgedPriorityNotes: number;
  hasActiveSafeReturn: boolean;
  removedFromCensus: boolean;
  keyDetails: string | null;
  constructor(options: Partial<LocationEpisodeOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.active = opts.active;
    this.archived = opts.archived;
    this.currentRehabState = new RehabState(opts.currentRehabState);
    this.endDate = parseDate(opts.endDate) as Date;
    this.episodeId = opts.episodeId;
    this.episodeClassifications = opts.episodeClassifications.map((ec) => new Classification(ec));
    this.hasNewChanges = opts.hasNewChanges;
    this.hospital = new Group(opts.hospital);
    this.lastValidPredecessor = opts.lastValidPredecessor;
    this.latest = opts.latest;
    this.lengthOfStay = opts.lengthOfStay;
    this.groupId = opts.groupId;
    this.group = new Group(opts.group);
    this.owner = new Group(opts.owner);
    this.patient = new Patient(opts.patient);
    this.planTypeClassification = opts.planTypeClassification ? new Classification(opts.planTypeClassification) : null;
    this.physicianTeam = new AttrValue(opts.physicianTeam);
    this.questionTemplates = opts.questionTemplates.map((x) => new QuestionTemplate(x));
    this.eligibleForProgressUpdate = opts.eligibleForProgressUpdate;
    this.rehabStates = opts.rehabStates.map((x) => new RehabState(x));
    this.reviews = opts.reviews.map((review) => ReviewModelFactory.get(review));

    this.startDate = opts.startDate;
    this.onTrack = opts.onTrack;
    this.onTrackUpdatedAt = opts.onTrackUpdatedAt;
    this.statusOutOfDate = opts.statusOutOfDate;
    this.assignedUsers = opts.assignedUsers?.map((x) => new LocationEpisodeUser(x)) || null;
    this.hasActiveServiceRefusals = opts.hasActiveServiceRefusals;
    this.admittedOn = parseDate(opts.admittedOn) as Date;
    this.admittedAt = parseDate(opts.admittedAt);
    this.createdAt = opts.createdAt ?? '';
    this.sharedRecords = (opts.sharedRecords || []).map((sr) => new LocationEpisode(sr));
    this.latestProgressUpdate = opts.latestProgressUpdate || null;
    this.trackLos = opts.trackLos;
    this.umAISummaryEnabled = opts.umAISummaryEnabled;
    this.unacknowledgedEscalations = opts.unacknowledgedEscalations ?? 0;
    this.unacknowledgedPriorityNotes = opts.unacknowledgedPriorityNotes ?? 0;
    this.hasActiveSafeReturn = opts.hasActiveSafeReturn ?? false;
    this.removedFromCensus = opts.removedFromCensus ?? false;
    this.keyDetails = opts.keyDetails ?? null;
  }

  get caseManagerUser() {
    return this.assignedUsers?.find((x) => x.userRole === AssignedUserRole.CaseManager) || null;
  }

  get utilizationManagerUser() {
    return this.assignedUsers?.find((x) => x.userRole === AssignedUserRole.UtilizationManager) || null;
  }

  get stayDescriptor() {
    if (this.archived) return 'Canceled';
    return this.latest ? 'Current' : 'Past';
  }

  get activeAuthorizationReview() {
    return this.reviews
      .filter((r): r is Authorization => (r.pending || r.accepted) && r instanceof Authorization)
      .sort((a, b) => {
        const dateA = a.createdAt?.getTime() ?? 0;
        const dateB = b.createdAt?.getTime() ?? 0;
        return dateB - dateA;
      })[0];
  }

  get projectedDischargeReview() {
    return this.reviews.find((r): r is ProjectedDischarge => r instanceof ProjectedDischarge);
  }

  get altcsApplicationReview() {
    return this.reviews.find((r): r is AltcsApplication => r instanceof AltcsApplication);
  }

  get safeReturnReview() {
    return this.reviews.find((r): r is SafeReturn => r instanceof SafeReturn);
  }

  get referralReviews() {
    return this.reviews
      .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0))
      .filter((r): r is Referral => r instanceof Referral);
  }

  get latestReferralReview(): Referral | undefined {
    return this.referralReviews[0];
  }

  get daysSinceEndDate() {
    return differenceInCalendarDays(new Date(), this.endDate);
  }

  get projectedLengthOfStay() {
    const projectedDate = this.projectedDischargeReview?.date;
    if (!projectedDate || !this.admittedAt) return null;
    return differenceInCalendarDays(projectedDate, this.admittedAt);
  }

  get discharged() {
    return this.currentRehabState.discharged;
  }

  get dischargedBeforeProjectedDate() {
    if (!this.discharged) return false;
    if (!this.projectedDischargeReview?.date) return false;
    const enteredAt = this.currentRehabState.enteredAt;
    return enteredAt && enteredAt < this.projectedDischargeReview?.date;
  }

  get dischargeOverdue() {
    if (!this.projectedDischargeReview?.date) return false;
    if (this.dischargedBeforeProjectedDate) return false;

    return new Date() > this.projectedDischargeReview.date;
  }

  get progressTemplate() {
    return (
      this.questionTemplates.find((template) => template.isProgress) ||
      new QuestionTemplate({ type: QuestionTemplateType.PROGRESS })
    );
  }

  get dischargeTemplate() {
    return this.questionTemplates.find((template) => template.isDischarge) || null;
  }

  get inTreatment() {
    return this.currentRehabState.inTreatment;
  }

  get inQueue() {
    return this.currentRehabState.queue;
  }
}
